import React, { useRef, useState } from "react";
import { Button, Input } from "antd";
import Title from "antd/lib/typography/Title";
import { useNavigate } from "react-router-dom";
import { useZxing } from "react-zxing";
import styled, { css } from "styled-components";

import { useQrScannerContext } from "../Context";
import { StyledElement as BackgroundContainer } from "pages/SocialService/components/reusables";
import { ENTITIES } from "constant/entity";
import { VideoQrScannerHeader } from "./videoQrScannerHeader";
import { desktop, tablet } from "utils/styles";
import { getCitizenCanRedeem } from "services/citizen";
import toast from "react-hot-toast";
import { useCitizenContext } from "pages/SocialService/Context";

const Container = styled(BackgroundContainer)`
  height: 100vh;
  position: absolute;
  z-index: 1;
  height: 100vh;
  width: 100%;

  background-image: none;
  padding-top: 0;
`;
const QrScanner = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
`;

const LogoutButton = styled(Button)`
  position: absolute;
  top: 15px;
  left: 15px;
  font-size: 12px;
  z-index: 2;
`;

const OverlayWrapperHeader = styled(Title)`
  text-align: center;
  color: white !important;
  margin: 0 !important;
`;
const Header = styled(OverlayWrapperHeader)`
  text-align: left;
`;

const SearchButton = styled(Button)`
  z-index: 2;
  margin-top: 15px;
  border-radius: 10px;
  width: 100%;

  ${({ theme }) => {
    const { colors, entity } = theme;
    let color = colors.primary;
    let hoverColor = colors.primaryHover;
    let textColor = "#fff";

    if ([ENTITIES.AMPING_KANUNAY, ENTITIES.KAUBAN_NATO, ENTITIES.AKAP_BIGAS].includes(entity)) {
      color = colors.secondary;
      hoverColor = colors.secondaryHover;
    }

    if (entity === ENTITIES.AMPING_KANUNAY) {
      textColor = "#000";
    }

    return css`
      background: ${color};
      border-color: ${color};

      &:hover,
      &:focus {
        background: ${hoverColor};
        border-color: ${hoverColor};
      }
      span {
        color: ${textColor};
      }
    `;
  }}
`;

const IdSearchInput = styled(Input)`
  margin-top: 10px;
`;
const Wrapper = styled.div`
  height: calc(100% - 165px);
  position: relative;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  overflow: hidden;
`;
const OverlayWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 20px;
  z-index: 2;

  background-color: transparent;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;

  * {
    pointer-events: auto;
  }
`;

const ScannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  transform: rotate(180deg);
  /* Combined overlay with background pattern and color */
  background: ${({ theme }) => {
    const { colors, entity } = theme;
    let color = colors.primary;

    switch (entity) {
      case ENTITIES.BPSF:
      case ENTITIES.TINGOG_47:
      case ENTITIES.MATIK_KANG_TINO:
      case ENTITIES.KAPMADUOLAN:
        color = colors.secondary;
        break;
    }

    if (entity === ENTITIES.AKAP_BIGAS) {
      return `${color}, url("/static/akapbigas/header-patterns.png")`;
    }

    return `${color}90`;
  }};

  ${({ theme }) => {
    const { entity } = theme;
    if (entity === ENTITIES.AKAP_BIGAS) {
      return `
        background-image: url("/static/akapbigas/header-patterns.png");
        background-repeat: no-repeat;
        background-size: cover;
        background-color: ${theme.colors.primary};
      `;
    }
  }}

  /* Single SVG clip path for the scanner cutout */
  clip-path: url(#rounded-cutout);

  ${tablet`
    clip-path: url(#rounded-cutout-tablet);
  `}

  ${desktop`
    clip-path: url(#rounded-cutout-desktop);
  `}
`;

// SVG clip paths for smoother rounded corners
const ClipPathSVG = () => (
  <svg width="0" height="0" style={{ position: "absolute" }}>
    <defs>
      {/* Mobile clip path */}
      <clipPath id="rounded-cutout" clipPathUnits="objectBoundingBox">
        <path
          d="
          M 0,0 
          L 1,0 
          L 1,1 
          L 0,1 
          Z
          M 0.05,0.47 
          L 0.05,0.83 
          Q 0.05,0.88 0.09,0.88
          L 0.91,0.88 
          Q 0.95,0.88 0.95,0.83
          L 0.95,0.47 
          Q 0.95,0.42 0.91,0.42
          L 0.09,0.42 
          Q 0.05,0.42 0.05,0.47
          Z
        "
        />
      </clipPath>

      {/* Tablet clip path */}
      <clipPath id="rounded-cutout-tablet" clipPathUnits="objectBoundingBox">
        <path
          d="
          M 0,0 
          L 1,0 
          L 1,1 
          L 0,1 
          Z
          M 0.2,0.35 
          L 0.2,0.85 
          Q 0.2,0.9 0.24,0.9
          L 0.76,0.9 
          Q 0.8,0.9 0.8,0.85
          L 0.8,0.35 
          Q 0.8,0.3 0.76,0.3
          L 0.24,0.3 
          Q 0.2,0.3 0.2,0.35
          Z
        "
        />
      </clipPath>

      {/* Desktop clip path */}
      <clipPath id="rounded-cutout-desktop" clipPathUnits="objectBoundingBox">
        <path
          d="
          M 0,0 
          L 1,0 
          L 1,1 
          L 0,1 
          Z
          M 0.35,0.35 
          L 0.35,0.65 
          Q 0.35,0.7 0.39,0.7
          L 0.61,0.7 
          Q 0.65,0.7 0.65,0.65
          L 0.65,0.35 
          Q 0.65,0.3 0.61,0.3
          L 0.39,0.3 
          Q 0.35,0.3 0.35,0.35
          Z
        "
        />
      </clipPath>
    </defs>
  </svg>
);

const FormWrapper = styled.div`
  align-self: center;
  width: 100%;
  max-width: 400px;
`;

const Overlay = ({ onRedeem }) => {
  const inputRef = useRef(null);

  return (
    <>
      <ScannerOverlay />
      <OverlayWrapper>
        <OverlayWrapperHeader level={3}>Scan QR Code</OverlayWrapperHeader>
        <FormWrapper>
          <Header level={4}>Couldn&apos;t find Citizen?</Header>
          <Header level={5}>Enter ID number instead.</Header>
          <IdSearchInput ref={inputRef} placeholder="Enter ID Number" />
          <SearchButton
            onClick={() => {
              onRedeem(inputRef.current.input.value.trim());
            }}>
            Search Citizen
          </SearchButton>
        </FormWrapper>
      </OverlayWrapper>
    </>
  );
};

const VideoQrScanner = () => {
  const { logout, user } = useQrScannerContext();
  const { dispatch } = useCitizenContext();
  const navigate = useNavigate();

  const onRedeem = async citizenId => {

    const canRedeem = await getCitizenCanRedeem(citizenId, user.id);
    if (canRedeem) {
      dispatch({ type: "SET_CAN_REDEEM", canRedeem });
      navigate(`/citizen/${citizenId}`);
    } else {
      toast.error("Citizen cannot redeem");
    }

  };

  const { ref } = useZxing({
    onError: error => {
      toast.error(error.message);
    },
    timeBetweenDecodingAttempts: 2000,
    constraints: {
      video: {
        facingMode: "environment",
      },
    },
    onDecodeResult(result) {
      if (result) {
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(result.getText())) {
          onRedeem(result.getText());
        } else {
          toast.error("Invalid QR Code");
        }
      }
    },
  });

  return (
    <Container>
      <ClipPathSVG />
      <VideoQrScannerHeader />
      <Wrapper>
        <Overlay onRedeem={onRedeem} />
        <QrScanner ref={ref} />
      </Wrapper>
      <LogoutButton
        style={{ zIndex: 5 }}
        onClick={() => {
          logout();
        }}
        size="small">
        Logout
      </LogoutButton>
    </Container>
  );
};

export default VideoQrScanner;
